import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'sonner';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { skillPaths } from '../data/learningContent';

interface Reflection {
  id: string;
  lesson_slug: string;
  path_slug: string | null;
  prompt_key: string;
  answer: string;
  created_at: string;
}

export default function WorkLibraryPage() {
  const { user } = useAuth();
  const [reflections, setReflections] = useState<Reflection[]>([]);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: 'Reflections — PromptGPT',
    description: 'Your own thinking, gathered.',
  });

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('reflections')
        .select('id, lesson_slug, path_slug, prompt_key, answer, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      setReflections((data as Reflection[]) || []);
      setLoading(false);
    })();
  }, [user]);

  const lessonTitleMap = useMemo(() => {
    const map = new Map<string, { title: string; pathSlug: string; pathTitle: string }>();
    skillPaths.forEach((p) =>
      p.modules.forEach((m) =>
        m.lessons.forEach((l) =>
          map.set(l.slug, { title: l.title, pathSlug: p.slug, pathTitle: p.title }),
        ),
      ),
    );
    return map;
  }, []);

  const remove = async (id: string) => {
    await supabase.from('reflections').delete().eq('id', id);
    setReflections((r) => r.filter((x) => x.id !== id));
    toast.success('Removed');
  };

  return (
    <div className="container-app py-10 max-w-3xl">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
        Reflections
      </div>
      <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-tight">
        Your own thinking, gathered.
      </h1>
      <p className="text-ink-600 text-lg leading-relaxed mb-10 max-w-2xl">
        Every lesson ends with two short prompts: what you noticed, and where you'll use it. They land here.
      </p>

      {loading ? (
        <div className="text-sm text-ink-500 py-12">Loading…</div>
      ) : reflections.length === 0 ? (
        <div className="bg-white border border-ink-100 rounded-2xl p-8 text-center">
          <p className="text-ink-600 mb-4">No reflections yet. Finish a lesson to start your own thread.</p>
          <Link to="/learn" className="btn-primary">
            Today <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {reflections.map((r) => {
            const meta = lessonTitleMap.get(r.lesson_slug);
            return (
              <article key={r.id} className="bg-white border border-ink-100 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-1">
                      {r.prompt_key === 'apply' ? 'Apply' : 'Reflect'}
                      {meta && <> · {meta.pathTitle}</>}
                    </div>
                    <div className="font-display text-lg leading-snug">
                      {meta?.title || r.lesson_slug}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(r.id)}
                    className="text-ink-400 hover:text-ink-700 p-1"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-ink-700 leading-relaxed mb-3 whitespace-pre-wrap">{r.answer}</p>
                <div className="flex items-center justify-between text-xs text-ink-500">
                  <span>{new Date(r.created_at).toLocaleDateString()}</span>
                  {meta && (
                    <Link
                      to={`/skill-paths/${meta.pathSlug}/${r.lesson_slug}`}
                      className="text-ink-700 hover:text-ink-900 inline-flex items-center gap-1"
                    >
                      Open lesson <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
